import {clsx} from 'clsx';
import {memo, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useLocation} from 'react-router';
import {useLocalStorage} from 'usehooks-ts';

import {
  hasPermission,
  NAVIGATION_POLICY_REQUEST,
  usePolicyCheck,
} from '#queries/policies/index.js';
import {Size} from '#theme';
import {Button, ButtonVariant} from '#ui/atoms/button/index.js';
import {Collapsible} from '#ui/atoms/collapsible/index.js';
import {Flex} from '#ui/atoms/flex/index.js';
import type {Icons} from '#ui/atoms/icon/icons.js';
import {Icon} from '#ui/atoms/icon/index.js';
import {Link} from '#ui/atoms/link/index.js';
import {ListButton} from '#ui/molecules/list_button/index.js';
import {getPersistedCollapsibleStorageKey} from '#utils/collapsible_storage.js';

import {useNavigationItems} from '../../hooks/use_navigation_items.js';
import type {NavItem} from '../../types.js';
import {TeamSwitcher} from '../team_switcher/index.js';
import {useNavigationContext} from './context.js';
import * as styles from './style.css.js';

const BASE_IMG_URL = '/assets/brand_base.png';
const BASE_ICON_URL = '/assets/brand_icon_base.png';
const BRAND_DEFAULT_WIDTH = 180;
const BRAND_ICON_WIDTH = 48;
const BRAND_ALT_TEXT = 'Growthloop spinning wheel icon';
const PRODUCT_REQUEST_SELECTOR = '.announcekit-product-requests-react';
const PRODUCT_REQUEST_ICON_URL = '/assets/comment_icon.svg';
const INVITE_TEAMMATES_ICON_URL = '/assets/invite_icon.svg';

type NavSectionStorageState = {
  collapsed: boolean;
};

function getNavSectionStorageKey(sectionKey: string): string {
  return getPersistedCollapsibleStorageKey(
    sectionKey ? `nav_section_${sectionKey}` : ''
  );
}

const Brand = memo(function Brand() {
  const {collapsed} = useNavigationContext();

  return (
    <div className={clsx(styles.brand, collapsed && styles.brandIcon)}>
      <img
        alt={BRAND_ALT_TEXT}
        src={BASE_IMG_URL}
        width={BRAND_DEFAULT_WIDTH}
        className={clsx(
          styles.brandImage,
          collapsed && styles.brandImageHidden
        )}
        loading="eager"
      />
      <img
        alt={BRAND_ALT_TEXT}
        src={BASE_ICON_URL}
        width={BRAND_ICON_WIDTH}
        className={clsx(
          styles.brandImage,
          !collapsed && styles.brandImageHidden
        )}
        loading="eager"
      />
    </div>
  );
});

const NavItem = memo(function NavItem({
  item,
  collapsed,
  isActive,
  isHardLink,
}: {
  item: {path: string; icon: Icons; label: string};
  collapsed: boolean;
  isActive: boolean;
  isHardLink?: boolean;
}) {
  const iconColor = isActive ? 'primary' : 'body';

  if (collapsed) {
    return (
      <ListButton
        href={item.path}
        selected={isActive}
        className={clsx(styles.navItem, styles.navItemCollapsed)}
        hardLink={isHardLink}
      >
        <Icon name={item.icon} size={Size.Fifteen} color={iconColor} />
      </ListButton>
    );
  }

  return (
    <ListButton
      href={item.path}
      selected={isActive}
      className={styles.navItem}
      hardLink={isHardLink}
    >
      <Flex gap={2} align="center">
        <Icon name={item.icon} size={Size.Eleven} color={iconColor} />
        <div className={styles.navItemLabel}>{item.label}</div>
      </Flex>
    </ListButton>
  );
});

const NavSection = memo(function NavSection({
  section,
  collapsed,
  pathname,
}: {
  section: {
    key: string;
    label: string;
    items: NavItem[];
  };
  collapsed: boolean;
  pathname: string;
}) {
  const isActiveSection = section.items.some(item =>
    pathname.includes(item.path.split('?')[0])
  );
  const sectionStorageKey = useMemo(
    () => getNavSectionStorageKey(section.key),
    [section.key]
  );
  const [storedSectionState, setStoredSectionState] =
    useLocalStorage<NavSectionStorageState>(sectionStorageKey, {
      collapsed: false,
    });
  const open = !storedSectionState.collapsed;

  const setOpen = useCallback(
    (nextOpen: boolean) => {
      setStoredSectionState({collapsed: !nextOpen});
    },
    [setStoredSectionState]
  );

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen);
    },
    [setOpen]
  );
  const autoOpenedPathRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isActiveSection) {
      autoOpenedPathRef.current = null;
      return;
    }

    if (autoOpenedPathRef.current === pathname) return;

    autoOpenedPathRef.current = pathname;
    if (!open) setOpen(true);
  }, [isActiveSection, open, pathname, setOpen]);

  return (
    <Collapsible
      key={section.key}
      className={styles.section}
      open={open}
      onOpenChange={handleOpenChange}
    >
      <div className={styles.sectionLabel}>
        <Collapsible.Anchor
          className={clsx(
            styles.sectionAnchor,
            collapsed && styles.sectionHeadingCollapsed
          )}
          label={collapsed ? '' : section.label}
        />
      </div>
      <Collapsible.Content>
        <ul className={styles.itemList}>
          {section.items.map(item => (
            <li key={item.path}>
              <NavItem
                item={item}
                collapsed={collapsed}
                isActive={pathname.includes(item.path.split('?')[0])}
                isHardLink={item.hardLink}
              />
            </li>
          ))}
        </ul>
      </Collapsible.Content>
    </Collapsible>
  );
});

const NavActions = memo(function NavActions({collapsed}: {collapsed: boolean}) {
  const {data: policyData} = usePolicyCheck(NAVIGATION_POLICY_REQUEST);
  const showProductRequests = hasPermission(
    policyData,
    'request_feature',
    'create'
  );
  const showInviteTeammates = hasPermission(
    policyData,
    'user_invitation',
    'new'
  );

  useEffect(() => {
    if (!showProductRequests) return;

    window.announcekit?.push({
      widget: 'https://changelog.growthloop.com/widgets/v2/2QExYQ',
      selector: PRODUCT_REQUEST_SELECTOR,
      badge: {style: {display: 'none'}},
    });
  }, [showProductRequests]);

  if (!showProductRequests && !showInviteTeammates) return null;

  const productRequestsContent = collapsed ? (
    <Icon name="comment" size={Size.Fifteen} color="body" />
  ) : (
    <Flex gap={2} align="center" className={styles.navActionContent}>
      <img
        alt=""
        aria-hidden="true"
        src={PRODUCT_REQUEST_ICON_URL}
        className={styles.navActionIcon}
      />
      Product Requests
    </Flex>
  );
  const inviteTeammatesContent = collapsed ? (
    <Icon name="forwardToInbox" size={Size.Fifteen} color="body" />
  ) : (
    <Flex gap={2} align="center" className={styles.navActionContent}>
      <img
        alt=""
        aria-hidden="true"
        src={INVITE_TEAMMATES_ICON_URL}
        className={styles.navActionIcon}
      />
      Invite teammates
    </Flex>
  );

  return (
    <div className={styles.navActions}>
      {showProductRequests ? (
        <Button
          type="button"
          variant={ButtonVariant.Outlined}
          className={clsx(
            styles.navActionItem,
            collapsed && styles.navActionItemCollapsed,
            PRODUCT_REQUEST_SELECTOR.slice(1)
          )}
          aria-label="Product Requests"
        >
          {productRequestsContent}
        </Button>
      ) : null}
      {showInviteTeammates ? (
        <Button
          asChild
          variant={ButtonVariant.Outlined}
          className={clsx(
            styles.navActionItem,
            collapsed && styles.navActionItemCollapsed
          )}
          aria-label="Invite teammates"
        >
          <Link href="/user_invitations/new" hardLink>
            {inviteTeammatesContent}
          </Link>
        </Button>
      ) : null}
    </div>
  );
});

export const Navigation = memo(function Navigation() {
  const {collapsed, setCollapsed, widthTransition} = useNavigationContext();
  const [focused, setFocused] = useState(false);
  const sections = useNavigationItems();
  const {pathname} = useLocation();

  const toggleCollapsed = useCallback(() => {
    setCollapsed(!collapsed);
  }, [collapsed, setCollapsed]);

  return (
    <aside
      onMouseEnter={() => setFocused(true)}
      onMouseLeave={() => setFocused(false)}
      className={clsx(
        styles.container,
        widthTransition && styles.containerWidthTransition,
        collapsed && styles.containerCollapsed
      )}
    >
      <nav className={styles.nav}>
        <Flex
          gap={1}
          direction={collapsed ? 'column' : 'row'}
          justify="between"
          align="center"
        >
          <Brand />
          {focused || collapsed ? (
            <button className={styles.toggleButton} onClick={toggleCollapsed}>
              <Icon
                size={16}
                name={collapsed ? 'chevronRight' : 'chevronLeft'}
                // eslint-disable-next-line react/forbid-component-props
                style={{'--color': 'currentColor'}}
              />
            </button>
          ) : null}
        </Flex>
        <div className={styles.teamSwitcher}>
          <TeamSwitcher />
        </div>
        {sections.map(section => (
          <NavSection
            key={section.key}
            section={section}
            collapsed={collapsed}
            pathname={pathname}
          />
        ))}
        <NavActions collapsed={collapsed} />
      </nav>
    </aside>
  );
});
